import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Loader2, Users, TrendingUp, GraduationCap, Briefcase, AlertTriangle, Crown, Shield, CheckCircle, ArrowUpRight, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, Area, AreaChart, ResponsiveContainer, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

import { API_BASE as API_URL } from '../lib/apiBase';
const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { 'Authorization': `Bearer ${t}` } : {};
};

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#06b6d4'];
const PIE_COLORS = ['#2563eb', '#16a34a', '#f59e0b'];

function KPICard({ icon: Icon, label, value, suffix, trend, color }) {
  return (
    <Card className="border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className={`p-2.5 rounded-xl ${color || 'bg-slate-100 dark:bg-slate-800'}`}>
            <Icon className="w-5 h-5 text-slate-700 dark:text-slate-300" />
          </div>
          {trend && (
            <Badge className={`text-xs ${parseFloat(trend) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {parseFloat(trend) >= 0 ? '+' : ''}{trend}
            </Badge>
          )}
        </div>
        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-3">{value}{suffix}</p>
        <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

function AttritionRiskBar({ entity, maxRate }) {
  const barColor = entity.rate > 4 ? '#dc2626' : entity.rate > 2 ? '#f59e0b' : '#16a34a';
  const width = maxRate > 0 ? (entity.rate / maxRate * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-sm w-40 truncate text-slate-700 dark:text-slate-300">{entity.entity_name}</span>
      <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-6 overflow-hidden">
        <div className="h-full rounded-full flex items-center px-2 transition-all" style={{ width: `${Math.max(width, 8)}%`, backgroundColor: barColor }}>
          <span className="text-xs font-bold text-white">{entity.rate}%</span>
        </div>
      </div>
    </div>
  );
}

function RiskProfileCard({ profile }) {
  const riskColor = profile.risk_score >= 75 ? 'bg-red-500' : profile.risk_score >= 50 ? 'bg-amber-500' : 'bg-yellow-500';
  const radarData = Object.entries(profile.radar || {}).map(([key, val]) => ({
    subject: key.charAt(0).toUpperCase() + key.slice(1), value: val, fullMark: 100
  }));

  return (
    <Card className="border-slate-200 dark:border-slate-700" data-testid={`risk-card-${profile.employee_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-slate-900 dark:text-white">{profile.name}</h3>
              <Badge className={`${riskColor} text-white text-xs`}>{profile.risk_score}% risk</Badge>
            </div>
            <p className="text-xs text-slate-500">{profile.designation} — {profile.department}</p>
            {profile.entity_name && <p className="text-xs text-slate-400">{profile.entity_name}</p>}
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-1 space-y-1.5">
            {profile.risk_factors.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <ArrowUpRight className="w-3 h-3 text-red-500 shrink-0" />
                <span className="text-slate-600 dark:text-slate-400">{f}</span>
              </div>
            ))}
          </div>
          {radarData.length > 0 && (
            <div className="w-36 h-28">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 8, fill: '#94a3b8' }} />
                  <Radar dataKey="value" stroke="#dc2626" fill="#dc2626" fillOpacity={0.15} strokeWidth={1.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SuccessionCard({ plan }) {
  const riskColors = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-emerald-100 text-emerald-700' };
  return (
    <Card className="border-slate-200 dark:border-slate-700" data-testid={`succ-card-${plan.role_title}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-slate-900 dark:text-white">{plan.role_title}</h3>
              <Badge className={`text-xs ${riskColors[plan.risk_level] || riskColors.medium}`}>{plan.risk_level} risk</Badge>
            </div>
            <p className="text-xs text-slate-500">Current: {plan.current_holder} {plan.entity_name && `— ${plan.entity_name}`}</p>
          </div>
          <Crown className="w-5 h-5 text-amber-500" />
        </div>
        <div className="space-y-4">
          {plan.candidates.map((c, i) => (
            <div key={i} className="border border-slate-100 dark:border-slate-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-bold text-blue-700">
                    {c.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.designation} — {c.department}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="outline" className="text-xs mb-0.5">{c.timeline}</Badge>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{c.readiness_pct}%</p>
                  <p className="text-xs text-slate-400">readiness</p>
                </div>
              </div>
              <Progress value={c.readiness_pct} className="h-1.5 mb-3" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-semibold text-emerald-600 mb-1">STRENGTHS</p>
                  {c.strengths.map((s, j) => (
                    <div key={j} className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" /> {s}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-600 mb-1">DEVELOPMENT GAPS</p>
                  {c.gaps.map((g, j) => (
                    <div key={j} className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                      <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" /> {g}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AIReport({ embedded = false }) {
  const ctx = useOutletContext() || {};
  const { selectedEntity } = ctx;
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const entityId = selectedEntity?.entity_id || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = entityId ? `?entity_id=${entityId}` : '';
      const res = await fetch(`${API_URL}/api/ai-reports/analytics${params}`, { headers: h() });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. This report is available only for HR, Admin, and Principal roles.');
        throw new Error('Failed to load analytics');
      }
      setData(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Generating AI Analytics...</span>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, workforce_composition, department_headcount, tenure_distribution, attrition_trend, attrition_by_entity, risk_profiles, succession_plans, is_global } = data;
  const maxAttrRate = Math.max(...attrition_by_entity.map(e => e.rate), 1);

  return (
    <div className="space-y-6" data-testid="ai-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">AI Analytics</h1>
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
              <BarChart3 className="w-3 h-3 mr-1" /> AI-Powered
            </Badge>
          </div>
          <p className="text-sm text-slate-500">
            {is_global
              ? 'Predictive insights for attrition, succession planning, and workforce optimization across all institutes'
              : `Comprehensive analytics for ${selectedEntity?.name || 'your institute'}`}
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <KPICard icon={Users} label="Avg Tenure" value={kpis.avg_tenure} suffix=" yrs" color="bg-blue-50 dark:bg-blue-900/20" />
        <KPICard icon={TrendingUp} label="Avg Workload" value={kpis.avg_workload} suffix="%" trend="+2%" color="bg-amber-50 dark:bg-amber-900/20" />
        <KPICard icon={GraduationCap} label="PhD Holders" value={kpis.phd_holders_pct} suffix="%" color="bg-emerald-50 dark:bg-emerald-900/20" />
        <KPICard icon={Briefcase} label="Open Positions" value={kpis.open_positions} color="bg-violet-50 dark:bg-violet-900/20" />
      </div>

      {/* Row 2: Workforce, Department, Tenure */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Workforce Composition */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Workforce Composition</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={workforce_composition} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {workforce_composition.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {workforce_composition.map((w, i) => (
                <div key={w.name} className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PIE_COLORS[i] }} />
                  <span className="text-slate-600 dark:text-slate-400">{w.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Department Headcount */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Department Headcount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={department_headcount} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="department" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Tenure Distribution */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Tenure Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tenure_distribution} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Attrition Trend + Attrition by Entity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Attrition Trend */}
        <Card className="lg:col-span-3 border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-red-500" /> Attrition Rate Trend & Prediction
              </CardTitle>
              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" /> Trend Analysis
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={attrition_trend} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="actual" name="Actual" stroke="#2563eb" fill="#2563eb" fillOpacity={0.1} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="predicted" name="AI Predicted" stroke="#dc2626" strokeDasharray="5 3" strokeWidth={2} dot={{ r: 3, fill: '#dc2626' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Attrition by Entity */}
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-600" /> Attrition Risk by Institute
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {attrition_by_entity.map(ent => (
                <AttritionRiskBar key={ent.entity_id} entity={ent} maxRate={maxAttrRate} />
              ))}
            </div>
            <div className="flex gap-4 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Low (&lt;2%)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Medium (2-4%)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> High (&gt;4%)</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Risk Profiles + Succession */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* High-Risk Attrition Profiles */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> High-Risk Attrition Profiles
            </h2>
            {risk_profiles.length > 0 && (
              <Badge className="bg-red-100 text-red-700 border-0 text-xs">{risk_profiles.length} flagged</Badge>
            )}
          </div>
          {risk_profiles.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm">No high-risk profiles detected</p>
            </CardContent></Card>
          ) : (
            risk_profiles.map(p => <RiskProfileCard key={p.employee_id} profile={p} />)
          )}
        </div>

        {/* Succession Planning */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" /> Succession Planning
            </h2>
            {succession_plans.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">{succession_plans.length} critical roles</Badge>
            )}
          </div>
          {succession_plans.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400">
              <Crown className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No succession plans configured yet</p>
            </CardContent></Card>
          ) : (
            succession_plans.map((s, i) => <SuccessionCard key={i} plan={s} />)
          )}
        </div>
      </div>
    </div>
  );
}
